import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Users, AlertTriangle, TrendingUp, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CountUp from "@/components/CountUp";

const fmt = (n) => (n == null ? null : Number(n).toLocaleString("es-AR"));

function Tarjeta({ icon: Icon, titulo, children, testid, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay }}
      className="card-dark p-6" data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon size={16} className="text-dorado shrink-0" />
        <h3 className="mono-label text-dorado">{titulo}</h3>
      </div>
      {children}
    </motion.div>
  );
}

export default function ResumenAndando({ resumen, nombre }) {
  const nav = useNavigate();
  const [bajando, setBajando] = useState(false);
  const r = resumen || {};
  const dep = r.dependencia || [];
  const top = dep[0];
  const frenos = r.frenos || [];
  const antes = r.antes_despues || [];

  const descargarManual = async () => {
    setBajando(true);
    try {
      const { data } = await api.get("/manual-empresa", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `manual-${(nombre || "mi-empresa").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Listo. Descargamos el manual de tu empresa.");
    } catch {
      toast.error("No pudimos generar el manual. Probá de nuevo.");
    } finally {
      setBajando(false);
    }
  };

  const plataMes = fmt(r.plata_mes);

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-xl p-8 mb-10 w-full"
        style={{ border: "1px solid rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.05)" }}
        data-testid="mesa-todo-andando"
      >
        <div className="mono-label text-dorado mb-4">YA TENÉS LO TUYO ANDANDO</div>
        <h2 className="titulo text-2xl mb-3" data-testid="andando-resumen-linea">
          {r.andando_count} {r.andando_count === 1 ? "proceso" : "procesos"}, {r.horas_total} horas por mes que volvieron a ser tuyas.
          {plataMes && <span className="text-dorado"> USD {plataMes} por mes.</span>}
        </h2>
        <p className="text-gris text-sm">Ahora empieza la parte que no viste todavía.</p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4 mb-10 w-full">
        {/* a) Manual */}
        <Tarjeta icon={FileText} titulo="EL MANUAL DE TU EMPRESA" testid="resumen-manual" delay={0.15}>
          <p className="text-gris text-sm leading-relaxed mb-5">
            Tus procesos, escritos paso a paso. Cuando entra alguien nuevo, en vez de tres días explicando, le das esto.
          </p>
          <button className="btn-dorado flex items-center gap-2 !py-2.5 !px-4" onClick={descargarManual} disabled={bajando} data-testid="descargar-manual">
            <Download size={14} /> {bajando ? "GENERANDO…" : "DESCARGAR EL MANUAL DE MI EMPRESA"}
          </button>
        </Tarjeta>

        {/* b) De quién depende */}
        <Tarjeta icon={Users} titulo="DE QUIÉN DEPENDE TU EMPRESA" testid="resumen-dependencia" delay={0.22}>
          {top && top.count >= 2 ? (
            <p className="text-crema text-sm leading-relaxed" data-testid="dependencia-frase">
              {top.count} de tus {r.dependencia_total_procesos} procesos pasan por <span className="text-dorado">{top.responsable}</span>.
              Si {top.responsable} se toma vacaciones, tu operación se frena en {top.count} lugares al mismo tiempo.
            </p>
          ) : dep.length > 0 ? (
            <div className="space-y-1.5">
              {dep.map((d, i) => (
                <div key={i} className="text-sm text-crema flex justify-between gap-3" data-testid={`dependencia-item-${i}`}>
                  <span>{d.responsable}</span>
                  <span className="text-gris">{d.count} {d.count === 1 ? "proceso" : "procesos"}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gris text-sm leading-relaxed">Todavía no marcaste quién hace cada paso. Cuando lo hagas, acá te mostramos de quién depende tu operación.</p>
          )}
        </Tarjeta>

        {/* c) Dónde se te frena */}
        <Tarjeta icon={AlertTriangle} titulo="DÓNDE SE TE FRENA" testid="resumen-frenos" delay={0.29}>
          {frenos.length > 0 ? (
            <div className="space-y-3">
              {frenos.map((f, i) => (
                <div key={i} data-testid={`freno-item-${i}`}>
                  <div className="text-crema text-sm">{f.paso}</div>
                  <div className="mono-label text-gris mt-0.5" style={{ fontSize: "10px" }}>EN «{f.proceso}»</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gris text-sm leading-relaxed">Ninguno de tus procesos tiene un cuello de botella marcado. Cuando definas uno donde algo te frene, lo vas a ver acá.</p>
          )}
        </Tarjeta>

        {/* d) El antes y después */}
        <Tarjeta icon={TrendingUp} titulo="EL ANTES Y DESPUÉS" testid="resumen-antes-despues" delay={0.36}>
          <div className="space-y-2 mb-4">
            {antes.map((a, i) => (
              <div key={i} className="flex justify-between gap-3 text-sm" data-testid={`antes-item-${i}`}>
                <span className="text-crema">{a.titulo}</span>
                <span className="mono-label text-gris shrink-0">{a.horas_mes} H/MES</span>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t border-borde flex justify-between items-baseline">
            <span className="mono-label text-gris">TOTAL RECUPERADO</span>
            <span className="font-mono text-dorado text-lg" data-testid="antes-total">
              <CountUp value={r.horas_total} /> h/mes
            </span>
          </div>
        </Tarjeta>
      </div>

      {/* El paso siguiente natural */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.44 }}
        className="w-full" data-testid="resumen-siguiente"
      >
        <p className="text-crema text-lg mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          ¿Qué otra cosa te sigue comiendo el día? Contámelo y lo sumo al mapa sin rehacer nada.
        </p>
        <button className="btn-dorado flex items-center gap-2" onClick={() => nav("/diagnostico?agregar=1")} data-testid="resumen-agregar-proceso">
          <Plus size={15} /> AGREGAR UN PROCESO
        </button>
      </motion.div>
    </div>
  );
}
